import { debounce } from 'throttle-debounce';
import getLocalePrefix from '../../lib/getLocalePrefix';
import { SETTINGS } from '../../utils/constants';

const initSearch = () => {
  const drawer = document.querySelector('[data-search-drawer]');
  if (!drawer) return;

  const overlay = document.querySelector('[data-search-drawer-overlay]');
  const closeBtn = drawer.querySelector('[data-search-drawer-close]');
  const drawerInput = drawer.querySelector('[data-search-drawer-input]');
  const popularEl = drawer.querySelector('[data-search-drawer-popular]');
  const resultsWrapper = drawer.querySelector('[data-search-drawer-results-wrapper]');
  const resultsEl = drawer.querySelector('[data-search-drawer-results]');
  const resultsTerm = drawer.querySelector('[data-search-drawer-results-term]');
  const footerEl = drawer.querySelector('[data-search-drawer-footer]');
  const seeAllLink = drawer.querySelector('[data-search-drawer-see-all]');
  const desktopTrigger = document.querySelector('[data-search-drawer-trigger]');
  const mobileTrigger = document.querySelector('[data-mobile-search-toggle]');

  if (!drawerInput || !resultsWrapper || !resultsEl || !popularEl || !footerEl) return;

  const settings = JSON.parse(drawer.dataset.settings);
  let isOpen = false;

  const showPopular = () => {
    resultsWrapper.classList.add('hidden', 'opacity-0');
    resultsEl.innerHTML = '';
    if (resultsTerm) resultsTerm.textContent = '';
    popularEl.classList.remove('hidden');
    footerEl.classList.add('hidden');
  };

  const setTriggerExpanded = (expanded) => {
    desktopTrigger?.setAttribute('aria-expanded', expanded);
    mobileTrigger?.setAttribute('aria-expanded', expanded);
  };

  const openDrawer = () => {
    if (isOpen) return;
    isOpen = true;
    drawer.classList.remove('translate-x-full');
    if (overlay) {
      overlay.classList.remove('opacity-0', 'pointer-events-none');
      overlay.classList.add('opacity-100');
    }
    setTriggerExpanded('true');
    document.activeElement?.blur();
    requestAnimationFrame(() => drawerInput?.focus());
  };

  const closeDrawer = () => {
    if (!isOpen) return;
    isOpen = false;
    drawer.classList.add('translate-x-full');
    if (overlay) {
      overlay.classList.remove('opacity-100');
      overlay.classList.add('opacity-0', 'pointer-events-none');
    }
    setTriggerExpanded('false');
    if (drawerInput) drawerInput.value = '';
    showPopular();
  };

  const showResults = (html, query) => {
    popularEl.classList.add('hidden');
    resultsEl.innerHTML = html;
    if (resultsTerm) resultsTerm.textContent = query;
    resultsWrapper.classList.remove('hidden');
    // rAF so the browser paints hidden→block before transitioning opacity
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resultsWrapper.classList.remove('opacity-0'));
    });
    footerEl.classList.remove('hidden');
  };

  const updateSeeAll = (query) => {
    if (!seeAllLink) return;
    const localePrefix = getLocalePrefix();
    seeAllLink.href = `${localePrefix}/search?q=${encodeURIComponent(query)}&options[prefix]=last`;
    const template = seeAllLink.dataset.seeAllTemplate || '';
    seeAllLink.textContent = template.replace('__TERMS__', query);
  };

  const fetchResults = async (query) => {
    const localePrefix = getLocalePrefix();
    const type = settings?.type || 'product';
    const fields = settings?.fields || 'title';
    const oos = settings?.oos || 'show';
    const url = `${window.location.origin}${localePrefix}/search/suggest?q=${encodeURIComponent(query)}&resources[type]=query,${type}&resources[options][fields]=${fields}&resources[limit]=6&resources[options][unavailable_products]=${oos}&section_id=search-drawer-results`;

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
      const htmlText = await response.text();
      const parsed = new DOMParser().parseFromString(htmlText, 'text/html');
      const section = parsed.querySelector('#shopify-section-search-drawer-results');
      if (section) {
        showResults(section.innerHTML, query);
        updateSeeAll(query);
        if (typeof window.ReloadSmartWishlist === 'function') {
          await window.ReloadSmartWishlist();
        }
      } else {
        showPopular();
      }
    } catch (err) {
      console.error('Search drawer fetch error:', err);
    }
  };

  const handleInput = () => {
    const value = drawerInput.value.trim();
    const trigger = settings?.trigger ?? 1;
    if (value.length < trigger) {
      showPopular();
      return;
    }
    fetchResults(value);
  };

  const debouncedHandleInput = debounce(SETTINGS.SEARCH_DEBOUNCE_DELAY, handleInput);

  const handleKeydown = (e) => {
    if (e.key === 'Escape' && isOpen) closeDrawer();
  };

  drawerInput.addEventListener('input', debouncedHandleInput);
  closeBtn?.addEventListener('click', closeDrawer);
  overlay?.addEventListener('click', closeDrawer);
  document.addEventListener('keydown', handleKeydown);
  document.querySelectorAll('[data-search-drawer-trigger]').forEach(el => el.addEventListener('click', openDrawer));
  mobileTrigger?.addEventListener('click', openDrawer);

  window.theme = window.theme || {};
  window.theme.search = {
    openDrawer,
    closeDrawer,
    destroy() {
      drawerInput.removeEventListener('input', debouncedHandleInput);
      closeBtn?.removeEventListener('click', closeDrawer);
      overlay?.removeEventListener('click', closeDrawer);
      document.removeEventListener('keydown', handleKeydown);
      desktopTrigger?.removeEventListener('click', openDrawer);
      mobileTrigger?.removeEventListener('click', openDrawer);
    }
  };
};

export default initSearch;
